import {AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, inject, ViewChild} from '@angular/core';
import {SelectPipe} from '@aviary/pipes/select.pipe';
import {OrganizationSelectors} from '@store/organization/organization.selectors';
import {NgTemplateOutlet} from '@angular/common';
import {NamedRouterLinkDirective} from '@lib/directives/named-router-link/named-router-link.directive';
import {HiddenUntilLoadedDirective} from '@aviary/directives/hidden-until-loaded.directive';
import {filter} from 'rxjs/operators';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {Store} from '@ngrx/store';

@Component({
  selector: 'px-header-nav-bar',
  host: {
    class: 'flex flex-row justify-center h-[3.75rem] w-full bg-ref-neutral-variant-40 text-title-large text-nowrap'
  },
  templateUrl: './header-nav-bar.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [SelectPipe, NamedRouterLinkDirective, NgTemplateOutlet, HiddenUntilLoadedDirective]
})
export class HeaderNavBarComponent implements AfterViewInit {
  @ViewChild('linkContainer') linkContainer!: ElementRef<HTMLElement>;
  private resizeObserver?: ResizeObserver;
  private changeDetector: ChangeDetectorRef = inject(ChangeDetectorRef);
  public readonly organizationSelectors = OrganizationSelectors.Auth;
  private readonly store: Store = inject(Store);
  private maxGap: number = 4.75;
  private minGap: number = 1.5;
  public gapInRem: number = this.maxGap;

  constructor() {
    this.store.select(this.organizationSelectors.loaded)
      .pipe(
        filter(Boolean),
        takeUntilDestroyed()
      )
      .subscribe(() => {
        this.updateGap();
      });
  }

  ngAfterViewInit(): void {
    // Set up a resize observer which updates the gap between links when the nav bar is resized
    this.resizeObserver = new ResizeObserver(() => {
      this.updateGap();
    });
    this.resizeObserver.observe(this.linkContainer.nativeElement);
  }

  // Sets the gap to be used between each nav link in rems
  private updateGap(): void {
    if (!this.linkContainer) {
      return;
    }
    const containerElement: HTMLElement = this.linkContainer.nativeElement;
    const navLinks: HTMLElement[] = Array.from(containerElement.children) as HTMLElement[];

    // At a small enough number of links, we don't need to calculate a gap.
    if (navLinks.length < 5) {
      this.gapInRem = this.maxGap;
      return;
    }

    // The total width (in rems) of all the links in the nav bar
    const totalLinkWidth = navLinks.reduce((total, link) => total + (link.offsetWidth / 16), 0);

    const availableSpace: number = (containerElement.clientWidth / 16) - totalLinkWidth;
    const maxPreferredGap: number = availableSpace / (navLinks.length - 1);

    // The determined final gap in rems between links.
    this.gapInRem = Math.max(
      this.minGap,                           // Don't return under the minimum gap size
      Math.min(this.maxGap, maxPreferredGap) // Don't return over the maximum gap size
    );

    this.changeDetector.markForCheck();
  }
}
